import { supabase } from '../lib/supabase'
import { AircraftService } from './intellijetnet/aircraft.service'
import { Aircraft as APIAircraft } from '../types/intellijetnet'
import { Aircraft as LocalAircraft, UnifiedAircraftData, MarketCategory, AircraftOffMarketInput } from '../types/database'

export class UnifiedAircraftService {
  /**
   * Get unified aircraft data for a specific model, combining API and off-market aircraft
   */
  static async getByModelId(modelId: number, options: {
    includeForSale?: boolean
    includeRecentlySold?: boolean
    includeRemovedFromMarket?: boolean
    includeOffMarket?: boolean
  } = {}): Promise<UnifiedAircraftData[]> {
    const {
      includeForSale = true,
      // These options are reserved for future use
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      includeRecentlySold: _includeRecentlySold = false,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      includeRemovedFromMarket: _includeRemovedFromMarket = false,
      includeOffMarket = true
    } = options

    try {
      const unifiedAircraft: UnifiedAircraftData[] = []

      // Get API aircraft for this model
      if (includeForSale) {
        const apiAircraft = await AircraftService.query()
          .eq('model_id', modelId)
          .eq('forsale', true)
          .eq('is_active', true)
          .execute<APIAircraft>()

        // Get local aircraft data for these aircraft
        const aircraftIds = apiAircraft.data.map(a => a.aircraft_id)
        const localAircraftMap = await this.getLocalAircraftMap(aircraftIds)

        // Combine API and local data
        for (const apiData of apiAircraft.data) {
          const localData = localAircraftMap.get(apiData.aircraft_id)
          
          // Skip if this aircraft is marked as off-market for a different model
          if (localData?.is_off_market && localData.off_market_model_id !== modelId) {
            continue
          }

          unifiedAircraft.push(this.combineAircraftData(apiData, localData, 'api'))
        }
      }

      // Get off-market aircraft for this model
      if (includeOffMarket) {
        const offMarketAircraft = await this.getOffMarketAircraft(modelId)
        
        for (const localData of offMarketAircraft) {
          // Check if we already have this aircraft from API data
          const existingIndex = unifiedAircraft.findIndex(a => a.aircraft_id === localData.aircraft_id)
          
          if (existingIndex >= 0) {
            // Mark as hybrid (exists in both API and off-market)
            unifiedAircraft[existingIndex].data_source = 'hybrid'
            unifiedAircraft[existingIndex].has_conflict = true
          } else {
            // Try to get API data for this aircraft
            const apiData = await AircraftService.getById(localData.aircraft_id)
            unifiedAircraft.push(this.combineAircraftData(apiData, localData, 'off_market'))
          }
        }
      }

      return unifiedAircraft

    } catch (error) {
      throw error
    }
  }

  /**
   * Get off-market aircraft by category for a specific model
   */
  static async getOffMarketByCategory(modelId: number, category: MarketCategory): Promise<UnifiedAircraftData[]> {
    try {
      const { data: localAircraft, error } = await supabase
        .from('aircraft')
        .select('*')
        .eq('is_off_market', true)
        .eq('off_market_model_id', modelId)
        .eq('off_market_category', category)

      if (error) {
        throw error
      }

      const unifiedAircraft: UnifiedAircraftData[] = []

      for (const localData of localAircraft || []) {
        // Try to get API data for this aircraft
        const apiData = await AircraftService.getById(localData.aircraft_id)
        unifiedAircraft.push(this.combineAircraftData(apiData, localData, 'off_market'))
      }

      return unifiedAircraft

    } catch (error) {
      throw error
    }
  }

  /**
   * Add an aircraft to off-market
   */
  static async addToOffMarket(input: AircraftOffMarketInput): Promise<LocalAircraft> {
    try {
      // Check if aircraft already exists in API data for this model
      const apiAircraft = await AircraftService.getById(input.aircraft_id)
      if (apiAircraft && apiAircraft.model_id === input.off_market_model_id && apiAircraft.forsale && apiAircraft.is_active) {
        throw new Error('Aircraft is already available for sale in API data. Cannot add to off-market.')
      }

      // Check if aircraft is already in off-market
      const { data: existingAircraft, error: checkError } = await supabase
        .from('aircraft')
        .select('*')
        .eq('aircraft_id', input.aircraft_id)
        .eq('is_off_market', true)
        .maybeSingle()

      if (checkError && checkError.code !== 'PGRST116') {
        throw new Error(`Error checking existing aircraft: ${checkError.message}`)
      }

      if (existingAircraft) {
        throw new Error('Aircraft is already in off-market')
      }

      // Get or create aircraft record
      const { data: aircraftRecord, error: upsertError } = await supabase
        .from('aircraft')
        .upsert({
          aircraft_id: input.aircraft_id,
          options: input.options || [],
          is_off_market: true,
          off_market_category: input.off_market_category,
          off_market_model_id: input.off_market_model_id,
          off_market_created_at: new Date().toISOString()
        }, {
          onConflict: 'aircraft_id'
        })
        .select()
        .single()

      if (upsertError) {
        throw upsertError
      }

      return aircraftRecord

    } catch (error) {
      throw error
    }
  }

  /**
   * Update off-market aircraft category
   */
  static async updateOffMarketCategory(aircraftId: number, category: MarketCategory): Promise<LocalAircraft> {
    try {
      const { data, error } = await supabase
        .from('aircraft')
        .update({ off_market_category: category })
        .eq('aircraft_id', aircraftId)
        .eq('is_off_market', true)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data

    } catch (error) {
      throw error
    }
  }

  /**
   * Remove aircraft from off-market
   */
  static async removeFromOffMarket(aircraftId: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('aircraft')
        .update({
          is_off_market: false,
          off_market_category: null,
          off_market_model_id: null,
          off_market_created_at: null,
          off_market_created_by: null
        })
        .eq('aircraft_id', aircraftId)
        .eq('is_off_market', true)

      if (error) {
        throw error
      }

      // Check if we should clean up the aircraft record
      await this.cleanupAircraftRecordIfNeeded(aircraftId)

    } catch (error) {
      throw error
    }
  }

  /**
   * Get local aircraft data for multiple aircraft IDs
   */
  private static async getLocalAircraftMap(aircraftIds: number[]): Promise<Map<number, LocalAircraft>> {
    if (aircraftIds.length === 0) return new Map()

    try {
      const { data: localAircraft, error } = await supabase
        .from('aircraft')
        .select('*')
        .in('aircraft_id', aircraftIds)

      if (error) {
        return new Map()
      }

      const map = new Map<number, LocalAircraft>()
      localAircraft?.forEach(aircraft => {
        map.set(aircraft.aircraft_id, aircraft)
      })

      return map

    } catch (error) {
      return new Map()
    }
  }

  /**
   * Get off-market aircraft for a specific model
   */
  private static async getOffMarketAircraft(modelId: number): Promise<LocalAircraft[]> {
    try {
      const { data: localAircraft, error } = await supabase
        .from('aircraft')
        .select('*')
        .eq('is_off_market', true)
        .eq('off_market_model_id', modelId)

      if (error) {
        throw error
      }

      return localAircraft || []

    } catch (error) {
      throw error
    }
  }

  /**
   * Combine API and local aircraft data
   */
  private static combineAircraftData(
    apiData: APIAircraft | null,
    localData: LocalAircraft | undefined,
    dataSource: 'api' | 'off_market' | 'hybrid'
  ): UnifiedAircraftData {
    return {
      // API data (if available)
      aircraft_id: apiData?.aircraft_id || localData?.aircraft_id || 0,
      model_id: apiData?.model_id || localData?.off_market_model_id || null,
      sernbr: apiData?.sernbr || null,
      regnbr: apiData?.regnbr || null,
      yearmfr: apiData?.yearmfr || null,
      make: apiData?.make || null,
      model: apiData?.model || null,
      forsale: apiData?.forsale || null,
      is_active: apiData?.is_active || false,

      // Local data
      options: localData?.options || [],
      is_off_market: localData?.is_off_market || false,
      off_market_category: localData?.off_market_category || null,
      off_market_model_id: localData?.off_market_model_id || null,
      off_market_created_at: localData?.off_market_created_at || null,
      off_market_created_by: localData?.off_market_created_by || null,

      // Metadata
      data_source: dataSource,
      has_conflict: dataSource === 'hybrid'
    }
  }

  /**
   * Clean up aircraft record if no longer needed
   */
  private static async cleanupAircraftRecordIfNeeded(aircraftId: number): Promise<void> {
    try {
      // Check if aircraft has any data that should be preserved
      const { data: aircraftRecord, error } = await supabase
        .from('aircraft')
        .select('options, is_off_market')
        .eq('aircraft_id', aircraftId)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') {
        return
      }

      if (!aircraftRecord) return

      // If aircraft has no options and is not off-market, we can remove it
      if ((!aircraftRecord.options || aircraftRecord.options.length === 0) && !aircraftRecord.is_off_market) {
        const { error: deleteError } = await supabase
          .from('aircraft')
          .delete()
          .eq('aircraft_id', aircraftId)

        if (deleteError) {
          // Error cleaning up aircraft record - silently ignore
        }
      }

    } catch (error) {
      // Error in cleanup - silently ignore
    }
  }
}
